import * as React from "react";
import { useEffect, useState } from "react";
import { Card, CardHeader } from "@mui/material";
import ExamDocumentTable, { IncludedExamLevel } from "./ExamDocumentTable";
import Season, { Exam } from "../../domain/Season";
import Account, { AccountRole } from "../../domain/Account";
import SeasonState from "../../domain/SeasonState";
import ExamLevel from "../../domain/ExamLevel";
import { getMyAssignments } from "../../gateway/marking";
import { useAccount } from "../account/AccountContext";
import AssignmentIcon from '@mui/icons-material/Assignment';
import CardContentNoPadding from "../common/CardContentNoPadding";
import { UUID } from "../../domain/UUID";
import { useManagedCentres } from "../centres/ManagedCentresContext";

interface Props {
  readonly season: Season,
  readonly onlyExamIds?: UUID<Exam> | UUID<Exam>[]
}

export default function ExamDocumentDownloadCard({ season, onlyExamIds }: Props) {
  const { account } = useAccount();
  const { managedCentres } = useManagedCentres();
  const [hasAssignments, setHasAssignments] = useState<boolean>(false);

  const managesAnyCentres = managedCentres !== undefined && managedCentres.length > 0;
  if (onlyExamIds !== undefined && !Array.isArray(onlyExamIds)) {
    onlyExamIds = [onlyExamIds];
  }

  useEffect(() => {
    if (account == undefined) {
      return;
    }

    if (Account.hasRole(account, AccountRole.MARKER) && season.exams[ExamLevel.ADVANCED] !== undefined) {
      getMyAssignments(season.exams[ExamLevel.ADVANCED].examId).then(a => setHasAssignments(a.length > 0));
    }
  }, [season.id, account?.id]);

  if (account == undefined) {
    return null;
  }

  const levels = computeIncludedExamLevels(season, account, hasAssignments, managesAnyCentres, onlyExamIds);

  if (levels.length === 0) {
    return null;
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title="Exam documents"
        subheader="Download exam questions and solutions"
        avatar={<AssignmentIcon />}
        sx={{ bgcolor: 'grey.200' }}
      />
      <CardContentNoPadding>
        <ExamDocumentTable seasonId={season.id} levels={levels} />
      </CardContentNoPadding>
    </Card>
  );
}

function computeIncludedExamLevels(season: Season, account: Account, hasAssignments: boolean, managesAnyCentres: boolean, onlyExamId: UUID<Exam>[] | undefined): IncludedExamLevel[] {
  const permittedLevels = computePermittedExamLevels(season.state, account, hasAssignments, managesAnyCentres);

  if (onlyExamId === undefined) {
    return permittedLevels;
  }

  return permittedLevels.filter(level => onlyExamId.includes(season.exams[level.level].examId));
}

function computePermittedExamLevels(seasonState: SeasonState, account: Account, hasAssignments: boolean, managesAnyCentres: boolean): IncludedExamLevel[] {
  if (Account.hasRole(account, AccountRole.ADMIN)) {
    return ExamLevel.roundOneValues().filter(level => level !== ExamLevel.OTHER).map(level => ({ level, includeSolutions: true }));
  }

  if (SeasonState.isBefore(seasonState, SeasonState.ROUND_1_TEST_PERIOD)) {
    return [];
  }

  if (SeasonState.isBetween(seasonState, SeasonState.ROUND_1_TEST_PERIOD, SeasonState.ROUND_1_ADVANCED_MARKING)) {
    if (Account.hasRole(account, AccountRole.CENTRE_MANAGER) && managesAnyCentres) {
      // Advanced round one questions
      return [{ level: ExamLevel.ADVANCED, includeSolutions: false }];
    }

    return [];
  }

  if (SeasonState.isBetween(seasonState, SeasonState.ROUND_1_ADVANCED_MARKING, SeasonState.ROUND_1_ADVANCED_RESULTS_PUBLICATION)) {
    let includedLevels: Map<ExamLevel, boolean> = new Map();
    if (Account.hasRole(account, AccountRole.CENTRE_MANAGER)) {
      // Advanced round one questions
      includedLevels.set(ExamLevel.ADVANCED, false);
    }

    if (Account.hasRole(account, AccountRole.MARKER) && hasAssignments) {
      // Advanced questions and solutions (if user is also centre manager, this supercedes the above)
      includedLevels.set(ExamLevel.ADVANCED, true);
    }

    return Array.from(includedLevels.entries()).map(([l, s]) => ({ level: l, includeSolutions: s }));
  }

  // ROUND_1_ADVANCED_RESULTS_PUBLICATION: Only Advanced Q&S (BFI not available yet)
  if (seasonState === SeasonState.ROUND_1_ADVANCED_RESULTS_PUBLICATION) {
    let includedLevels: Map<ExamLevel, boolean> = new Map();
    if (Account.hasRole(account, AccountRole.CENTRE_MANAGER) && managesAnyCentres) {
      // Advanced questions and solutions only
      includedLevels.set(ExamLevel.ADVANCED, true);
    }

    if (Account.hasRole(account, AccountRole.MARKER) && hasAssignments) {
      includedLevels.set(ExamLevel.ADVANCED, true);
    }

    return Array.from(includedLevels.entries()).map(([l, s]) => ({ level: l, includeSolutions: s }));
  }

  // ROUND_1_NON_ADVANCED_TEST_PERIOD onwards: All Q&S available
  if (SeasonState.isOrIsAfter(seasonState, SeasonState.ROUND_1_NON_ADVANCED_TEST_PERIOD)) {
    let includedLevels: Map<ExamLevel, boolean> = new Map();
    if (Account.hasRole(account, AccountRole.CENTRE_MANAGER)) {
      // All questions and solutions
      ExamLevel.roundOneValues().filter(level => level !== ExamLevel.OTHER).forEach(level => includedLevels.set(level, true));
    }

    if (Account.hasRole(account, AccountRole.MARKER) && hasAssignments) {
      // Advanced questions and solutions (if user is also centre manager, this supplements the above)
      includedLevels.set(ExamLevel.ADVANCED, true);
    }

    return Array.from(includedLevels.entries()).map(([l, s]) => ({ level: l, includeSolutions: s }));
  }

  return [];
}
